import classNames from 'classnames';
import { useFormik } from 'formik';
import { FC, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CantoiaLogo from '../../../assets/img/logos/cantoia.png';
import MESSAGES from '../../../common/data/constants/messages';
import { ICognito } from '../../../common/data/dtos/auth';
import Button from '../../../components/bootstrap/Button';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import { showSuccess, showWarning } from '../../../components/extras/Notifications';
import AuthContext from '../../../contexts/authContext';
import useDarkMode from '../../../hooks/useDarkMode';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import authService from '../../../services/auth-service';

export const FirstAccessPage = () => {

    const { darkModeStatus } = useDarkMode();

    const { user } = useContext(AuthContext);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const navigate = useNavigate();
    const navigateToLogin = useCallback(() => navigate('/auth-pages/login'), [navigate]);
    const handleOnBackClick = useCallback(() => navigate('/auth-pages/login'), [navigate]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            password: '',
            confirmPassword: '',
        },
        validate: (values) => {
            const errors: { password?: string; confirmPassword?: string; email?: string } = {};

            if (!values.password) {
                errors.password = MESSAGES.VALIDATION.REQUIRED_FIELD;
            }

            if (!values.confirmPassword) {
                errors.confirmPassword = MESSAGES.VALIDATION.REQUIRED_FIELD;
            }

            if (values.password !== values.confirmPassword) {
                errors.confirmPassword = MESSAGES.VALIDATION.NOT_MATCHING_PASSWORD;
            }

            return errors;
        },
        validateOnChange: false,
        onSubmit: (values) => {
            handleCreatePassword(values);
        },
    });

    const handleCreatePassword = async (values: any) => {
        try {
            const model: ICognito = {
                password: values.password,
                oldPassword: values.confirmPassword,
                accessToken: '',
                code: '',
                email: user,
            };

            const response = await authService.assignPassword(model);
            if (!response.success) {
                showWarning(response.message ?? MESSAGES.LOGIN.NEW_PASSWORD_ERROR);
            } else {
                showSuccess(MESSAGES.LOGIN.NEW_PASSWORD_SUCCESS);
                navigateToLogin();
            }
        } catch (error) {
            showWarning(MESSAGES.LOGIN.NEW_PASSWORD_ERROR);
        }
    };

    const SetPasswordHeader: FC = () => {
        return (
            <>
                <div className="text-center h4 fw-bold mt-5">Primeiro acesso</div>

                <div className="text-center h5 text-muted mb-5">Insira seu e-mail e senha para continuar</div>
            </>
        );
    };

    const checkSpecial = (password: string) => {
        const specials = '^$*.[\\]{}()?-"!@#%&/\\\\,><\':;|_~`+=';
        const hasSpecial = new RegExp(`[${specials.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&')}]`).test(password);
        const hasMiddleSpace = password.includes(' ') && password.trim() !== password;
        return hasSpecial || hasMiddleSpace;
    };

    const isPasswordValid = (password: string) => {
        return (
            password.length >= 8 &&
            /\d/.test(password) &&
            /[a-z]/.test(password) &&
            /[A-Z]/.test(password) &&
            checkSpecial(password)
        );
    };

    return (
        <PageWrapper isProtected={false} title="Primeiro Acesso" className={classNames({ 'bg-dark': !darkModeStatus, 'bg-light': darkModeStatus })}>
            <Page>
                <div className="row h-100 align-items-center justify-content-center">
                    <div className="col-xl-4 col-lg-6 col-md-8 shadow-3d-container">
                        <Card className="shadow-3d-dark" data-tour="first-access-page">
                            <CardBody>
                                <div className="text-center my-1">
                                    <img src={CantoiaLogo} alt='Cantoia' width={300} />
                                </div>

                                <SetPasswordHeader />

                                <form className="row g-4">
                                    <>
                                        <div className="col-12">
                                            <FormGroup id="first-access-password" isFloating label="Senha">
                                                <Input
                                                    type="password"
                                                    name="password"
                                                    value={formik.values.password}
                                                    isTouched={formik.touched.password}
                                                    invalidFeedback={formik.errors.password}
                                                    isValid={formik.isValid}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    onFocus={() => {
                                                        formik.setErrors({});
                                                    }}
                                                    validFeedback=""
                                                />
                                            </FormGroup>
                                        </div>

                                        <div className="col-12">
                                            <FormGroup id="first-access-confirm-password" isFloating label="Confirmar Senha">
                                                <Input
                                                    type="password"
                                                    name="confirmPassword"
                                                    value={formik.values.confirmPassword}
                                                    isTouched={formik.touched.confirmPassword}
                                                    invalidFeedback={formik.errors.confirmPassword}
                                                    isValid={formik.isValid}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    onFocus={() => {
                                                        formik.setErrors({});
                                                    }}
                                                />
                                            </FormGroup>
                                        </div>


                                        <div className="col-12">
                                            <ul className="mt-2 small">
                                                <li style={{ color: formik.values.password.length >= 8 ? 'green' : 'red' }}>
                                                    Mínimo de 8 caracteres
                                                </li>
                                                <li style={{ color: /\d/.test(formik.values.password) ? 'green' : 'red' }}>
                                                    Conter ao menos 1 número
                                                </li>
                                                <li style={{ color: /[a-z]/.test(formik.values.password) ? 'green' : 'red' }}>
                                                    Conter ao menos 1 letra minúscula
                                                </li>
                                                <li style={{ color: /[A-Z]/.test(formik.values.password) ? 'green' : 'red' }}>
                                                    Conter ao menos 1 letra maiúscula
                                                </li>
                                                <li style={{ color: checkSpecial(formik.values.password) ? 'green' : 'red' }}>
                                                    Conter ao menos 1 caractere especial
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="col-12">
                                            <Button
                                                color="info"
                                                className="w-100 py-3"
                                                onClick={formik.submitForm}
                                                isDisable={!isPasswordValid(formik.values.password) || !formik.isValid}
                                            >
                                                Confirmar
                                            </Button>
                                        </div>

                                        <div className='col-12'>
                                            <Button
                                                color="info"
                                                isOutline={true}
                                                className="py-3 flex-grow-1 w-100"
                                                isDisable={isLoading}
                                                onClick={handleOnBackClick}
                                            >
                                                Voltar
                                            </Button>
                                        </div>
                                    </>

                                </form>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Page>
        </PageWrapper>
    );
};
